import { useEffect } from 'react';

// Adi #3 — scroll the list while a story is being dragged near its edges.
//
// react-dnd's HTML5 backend does no auto-scrolling: the browser only scrolls a
// container during a native drag if the pointer is already inside it AND the
// platform decides to, which in practice it does not here. So reordering a
// story more than a screenful meant dropping it, scrolling, picking it up
// again — about three rows per attempt.
//
// Driven by `dragover` rather than a timer started on `dragstart`, because
// dragover fires continuously with live coordinates for the whole drag and
// stops on its own when the drag ends. That means no interval to leak if the
// drop happens outside the window or the drag is cancelled with Escape.
export default function useDragAutoScroll(getContainer, {
  edgeSize = 90,      // px from an edge where scrolling begins
  maxSpeed = 22,      // px per frame at the very edge
} = {}) {
  useEffect(() => {
    let frame = null;
    let velocity = 0;

    const step = () => {
      const el = getContainer();
      if (!el || velocity === 0) { frame = null; return; }
      el.scrollTop += velocity;
      frame = requestAnimationFrame(step);
    };

    const onDragOver = (e) => {
      const el = getContainer();
      if (!el) return;
      const r = el.getBoundingClientRect();
      const y = e.clientY;
      // Outside the container entirely — stop rather than scrolling blind.
      if (y < r.top || y > r.bottom || e.clientX < r.left || e.clientX > r.right) {
        velocity = 0;
        return;
      }
      const fromTop = y - r.top;
      const fromBottom = r.bottom - y;
      if (fromTop < edgeSize) {
        // Ramp with proximity so a small overshoot does not fling the list.
        velocity = -Math.ceil(maxSpeed * ((edgeSize - fromTop) / edgeSize));
      } else if (fromBottom < edgeSize) {
        velocity = Math.ceil(maxSpeed * ((edgeSize - fromBottom) / edgeSize));
      } else {
        velocity = 0;
      }
      if (velocity !== 0 && frame === null) frame = requestAnimationFrame(step);
    };

    const stop = () => {
      velocity = 0;
      if (frame !== null) { cancelAnimationFrame(frame); frame = null; }
    };

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', stop);
    document.addEventListener('dragend', stop);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', stop);
      document.removeEventListener('dragend', stop);
      stop();
    };
  }, [getContainer, edgeSize, maxSpeed]);
}
